/** Enemy auras: the Healer's heal and the Shieldbearer's shield wall (DESIGN §5). */
import { Fix64 } from "../../maths/fix64";
import { SimEventKind } from "../events";
import { SimConstants } from "../constants";
import { distanceSquared } from "../../world/roadGeometry";
import type { LevelSim } from "../levelSim";
import { hexedHeal } from "./hex";

/**
 * Healers restore `healFractionPerSecond` of max HP per second to every other living enemy within their radius.
 * Runs after towers have fired so healing resolves after damage in the same tick; healers iterate in slot order.
 * The Starfall Wyrm (M40, a `legend`) is no raider of theirs: they never heal it (its HP is set against the board).
 */
export function tickHealers(sim: LevelSim): void {
  for (const healer of sim.enemySlots) {
    if (!healer.alive) continue;
    const a = sim.balance.enemies[healer.archetype];
    if (!a.healRadius.isPositive() || !a.healFractionPerSecond.isPositive()) continue;
    const radius2 = a.healRadius.mul(a.healRadius);
    for (const e of sim.enemySlots) {
      if (!e.alive || e === healer || e.hp.ge(e.maxHp) || sim.balance.enemies[e.archetype].legend) continue;
      const dx = e.x.sub(healer.x), dy = e.y.sub(healer.y);
      if (dx.mul(dx).add(dy.mul(dy)).gt(radius2)) continue;
      const amount = Fix64.min(hexedHeal(healer, e, e.maxHp.mul(a.healFractionPerSecond).divInt(SimConstants.TicksPerSecond)), e.maxHp.sub(e.hp));
      e.hp = e.hp.add(amount);
      sim.events.add(SimEventKind.EnemyHealed, e.id, healer.id, amount.floorToInt());
    }
  }
}

/**
 * Shield wall: every living enemy other than the bearer, within a bearer's radius, gains the bearer's armour bonus for
 * this tick. Bonuses do not stack (the largest applies), so a column of bearers is not invulnerable — it is merely a
 * group that physical damage struggles with, which is the point: bring burn or magic.
 */
export function tickShields(sim: LevelSim): void {
  for (const e of sim.enemySlots) if (e.alive) e.shieldArmour = Fix64.Zero;
  for (const bearer of sim.enemySlots) {
    if (!bearer.alive) continue;
    const a = sim.balance.enemies[bearer.archetype];
    if (!a.shieldRadius.isPositive() || !a.shieldArmour.isPositive()) continue;
    const r2 = a.shieldRadius.mul(a.shieldRadius);
    for (const e of sim.enemySlots) {
      if (!e.alive || e === bearer || e.isAir !== bearer.isAir || e.shieldArmour.ge(a.shieldArmour)) continue;
      if (distanceSquared(bearer.x, bearer.y, e.x, e.y).le(r2)) e.shieldArmour = a.shieldArmour;
    }
  }
}
